/**
 * Sonner-backed toaster — the shadcn-recommended toast primitive.
 *
 * The exported `Toaster` is the single instance that should be mounted
 * once at the app root. After that, any module imports `toast` from
 * `@flint/ui` and fires `toast.success('…')` / `toast.error('…')` /
 * `toast(…)` without worrying about portal wiring.
 *
 * Sonner ships with its own animations (in/out + swipe-to-dismiss) and
 * stack management, so consumers don't need to roll their own banner
 * + setTimeout cleanup.
 *
 * @see https://sonner.emilkowal.ski/ — API reference
 * @see https://ui.shadcn.com/docs/components/sonner — shadcn wrapper this mirrors
 */
import * as React from "react";
import { Toaster as SonnerToaster, toast } from "sonner";

export type ToasterProps = React.ComponentProps<typeof SonnerToaster>;

/**
 * Mount-once toaster. Themed with the project's tokens via CSS vars so
 * dark mode + brand accent come through without extra config.
 */
export function Toaster(props: ToasterProps): React.ReactElement {
  return (
    <SonnerToaster
      position="bottom-right"
      richColors
      closeButton
      toastOptions={{
        classNames: {
          toast:
            "group toast group-[.toaster]:bg-paper group-[.toaster]:text-ink group-[.toaster]:border-line group-[.toaster]:shadow-lg",
          description: "group-[.toast]:text-muted",
          actionButton: "group-[.toast]:bg-ink group-[.toast]:text-paper",
          cancelButton: "group-[.toast]:bg-bg group-[.toast]:text-muted",
        },
      }}
      {...props}
    />
  );
}

export { toast };
